import { ArrowLeft, ArrowRight, Trash } from '@phosphor-icons/react';

import { Button } from '../ui/Button';
import { cn } from '../../utils/cn';
import pt from '../../locales/pt';

/**
 * `.pen` `ds/wizard/footer` (CKuc0): topo com `$border`, `pt-$space-4`,
 * `gap-$space-2`.
 *
 * Duas composições, ambas presentes nas instâncias reais:
 * - com `onBack`: linha `ghost Voltar` + `primary Continuar` (fill), `gap-$space-3`;
 * - sem `onBack`: coluna com o primário a toda a largura e o ghost de saltar por baixo.
 *
 * `destructiveLabel`+`onDestructive` acrescentam a linha de acção destrutiva
 * de `ds/wizard/footer--destructive` (KIJVB) — mesma linha buttons + uma
 * segunda linha centrada (ícone + label `$danger-fg`, sem fundo/botão).
 * .pen: KIJVB (Selfie · Edição · Confirmação)
 */
export function WizardStepFooter({
  primaryLabel,
  primaryIcon: PrimaryIcon = ArrowRight,
  onPrimary,
  onSkip,
  skipLabel,
  onBack,
  backLabel,
  loading = false,
  disabled = false,
  destructiveLabel,
  destructiveIcon: DestructiveIcon = Trash,
  onDestructive,
  destructiveDisabled = false,
  className,
}) {
  const hasDestructive = Boolean(destructiveLabel && onDestructive);
  const copy = pt.wizard.common;
  const hasBack = typeof onBack === 'function';

  const primaryButton = (
    <Button
      type="button"
      variant="primary"
      size="md"
      onClick={onPrimary}
      loading={loading}
      disabled={disabled}
      className={cn('justify-center', hasBack ? 'min-w-0 flex-1' : 'w-full')}
    >
      {primaryLabel || copy.continue}
      {PrimaryIcon ? <PrimaryIcon className="size-[18px] shrink-0" weight="regular" aria-hidden /> : null}
    </Button>
  );

  const skipButton = onSkip ? (
    <Button
      type="button"
      variant="ghost"
      size="md"
      onClick={onSkip}
      disabled={loading}
      className="w-full justify-center text-type-body-sm font-medium"
    >
      {skipLabel || copy.skipLater}
    </Button>
  ) : null;

  return (
    <div
      data-ds-id={hasDestructive ? 'ds/wizard/footer--destructive' : 'ds/wizard/footer'}
      className={cn('flex flex-col gap-space-2 border-t border-border pt-space-4', className)}
    >
      {hasBack ? (
        <div className="flex items-center gap-space-3">
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={onBack}
            disabled={loading}
            className="shrink-0"
          >
            <ArrowLeft className="size-[18px] shrink-0" weight="regular" aria-hidden />
            {backLabel || pt.onboarding?.common?.back || pt.common.back}
          </Button>
          {primaryButton}
        </div>
      ) : (
        primaryButton
      )}
      {skipButton}
      {hasDestructive ? (
        <button
          type="button"
          onClick={onDestructive}
          disabled={loading || destructiveDisabled}
          className="flex items-center justify-center gap-space-1 pt-space-1 text-type-body-sm font-semibold text-danger-fg disabled:opacity-50"
        >
          <DestructiveIcon className="size-4 shrink-0" weight="regular" aria-hidden />
          {destructiveLabel}
        </button>
      ) : null}
    </div>
  );
}

export default WizardStepFooter;
